"use client"
import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { usePathname } from "next/navigation"
import { FC, useRef, useState, useCallback } from "react"
import Footer from "./Footer"
import type { FooterData } from "./Footer"
import Header from "./Header"
import type { HeaderData } from "./Header"
import { LayoutVisibilityProvider, useLayoutVisibility } from "./HeaderFooterVisibility"
import SideMenu from "./SideMenu"
import type { SideMenuData } from "./SideMenu"
import Search from "./Search"
import { ContactModalProvider } from "./ContactModal/context"
import { HEADER_HEIGHT_MOBILE, HEADER_HEIGHT_DESKTOP } from "./Header/styles"
import { breakpoints } from "../theme/mediaQueries"
import type { components } from "umbraco"

const StyledButton = styled(Button)`
  position: fixed;
  z-index: -10;
  background-color: black;
  color: white;
  top: -100px;
  &:focus-visible {
    z-index: 9999999;
    top: 0px;
  }
`
const StyledMain = styled.div<{
  isHomepage: boolean
  noPadding?: boolean
  fullHeight?: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${(p) => (p.fullHeight ? "100%" : "")};
  width: 100%;
  flex: 1;
  padding-top: ${(p) => (p.noPadding ? 0 : `${HEADER_HEIGHT_MOBILE}px`)};
  ${(p) =>
    !p.noPadding &&
    `@media (min-width: ${breakpoints.lg}px) {
      padding-top: ${HEADER_HEIGHT_DESKTOP}px;
    }`}
  :focus {
    outline: 0;
  }
`

const EmbedFooterSpace = styled.div`
  height: 200px;
`

export interface LayoutProps {
  locale: string
  children: React.ReactNode
  footerData?: FooterData | null
  headerData?: HeaderData | null
  sideMenuData?: SideMenuData | null
  contactModal?:
    | components["schemas"]["ContactModalPropertiesModel"]
    | null
  /**
   * EGDE-612: server-computed chrome visibility. Resolved from the page's CMS
   * hideHeader/hideFooter flags before this renders, so a chrome-less page never
   * emits a header or footer into the HTML at all.
   */
  showHeader?: boolean
  showFooter?: boolean
}

const LayoutInner: FC<LayoutProps> = ({ locale, children, footerData, headerData, sideMenuData, contactModal }) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathName = usePathname()
  const isHomepage = pathName === "/" || /^\/[a-z]{2}\/?$/.test(pathName)
  const { showHeader, showFooter } = useLayoutVisibility()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true)
  }, [])

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false)
  }, [])

  const handleClick = () => {
    if (mainRef.current) {
      mainRef.current.focus()
    }
  }

  const noHeader = !showHeader || !headerData

  return (
    <ContactModalProvider data={contactModal ?? null}>
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <StyledButton id="skip-to-content" tabIndex={3} onClick={handleClick}>
        {locale === "he" ? "דלג לתוכן המרכזי" : "skip navigation"}
      </StyledButton>
      {!noHeader && (
        <Header
          data={headerData}
          locale={locale}
          isHomepage={isHomepage}
          onOpenMenu={handleOpenMenu}
          onOpenSearch={handleOpenSearch}
        />
      )}
      {!noHeader && (
        <SideMenu
          data={sideMenuData}
          headerData={headerData}
          isOpen={isMenuOpen}
          onClose={handleCloseMenu}
          onOpenSearch={handleOpenSearch}
        />
      )}
      {!noHeader && (
        <Search
          locale={locale}
          open={isSearchOpen}
          onClose={handleCloseSearch}
          placeholder={
            (headerData?.properties as { searchPlaceholder?: string | null })
              ?.searchPlaceholder
          }
        />
      )}
      <StyledMain
        id="mainContent"
        tabIndex={-1}
        ref={mainRef}
        role="main"
        isHomepage={isHomepage}
        noPadding={isHomepage || noHeader}
        data-eseim-main
      >
        {children}
      </StyledMain>
      {showFooter && footerData?.properties && (
        <Footer
          data={footerData.properties}
          menuData={footerData.menuItems}
        />
      )}
    </div>
    </ContactModalProvider>
  )
}

const Layout: FC<LayoutProps> = ({
  showHeader = true,
  showFooter = true,
  ...props
}) => (
  <LayoutVisibilityProvider
    initialShowHeader={showHeader}
    initialShowFooter={showFooter}
  >
    <LayoutInner {...props} />
  </LayoutVisibilityProvider>
)

export default Layout
